import {ASSET_MAP, AssetMap} from '@core/constants/client-asset-maps';
import {RouteNames} from '@core/constants/routes';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Output,
} from '@angular/core';
import {LanguageService} from '@core/services/language.service';
import {environment} from '@environments/environment';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Store} from '@ngxs/store';
import {BlockedCountiresState} from '@store/blocked-countries/blocked-countries.state';
import {combineLatest, filter, map, of, shareReplay, switchMap} from 'rxjs';
import {LoadGlobalPageElementBySlug} from '@store/global-page-element/global-page-element.actions';
import {
  UmpireComponentBranding,
  UmpireComponentFooter,
  UmpireComponentTypeEnum,
  UmpireGlobalPageElementSlugEnum,
  UmpireLinkTypeEnum,
} from '@tisa-group/models';
import {GlobalPageElementState} from '@store/global-page-element/global-page-element-state.service';
import {TrackingService} from '@core/services/tracking.service';
import {CurrentClientState} from '@store/current-client/current-client.state';
import {clientLegalDocuments} from '@core/constants/legal-documents';
import {clientContactEmails} from '@core/constants/clients';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TranslatePipe],
  standalone: false,
})
export class FooterComponent {
  readonly templatePagesSlugsBlockedByCountry =
    environment.clientConfig.templatePagesSlugsBlockedByCountry;

  readonly isGdprClient = this.trackingService.isGdprClient;

  readonly RouteNames = RouteNames;

  @Output() cookieSettingsClicked = new EventEmitter<void>();

  // Static PDFs kept in the repo for clients that don't serve them from Wicket.
  clientDocuments$ = this.store
    .select(CurrentClientState.getData)
    .pipe(map(currentClient => clientLegalDocuments[currentClient?.id || ''] ?? {}));

  // Empty when neither the client nor its translations provide a support address.
  contactEmail$ = combineLatest([
    this.store.select(CurrentClientState.getData),
    this.translateService.stream('footer.email'),
  ]).pipe(
    map(([currentClient, translatedEmail]) => {
      const clientEmail = clientContactEmails[currentClient?.id || ''];

      return clientEmail || (translatedEmail === 'footer.email' ? '' : translatedEmail);
    }),
  );

  footerLogo$ = this.store
    .select(
      GlobalPageElementState.getGlobalPageElementBySlug(
        UmpireGlobalPageElementSlugEnum.BRANDING,
      ),
    )
    .pipe(
      map(
        globalPageElement =>
          globalPageElement?.components?.find(
            component => component.kind === UmpireComponentTypeEnum.BRANDING,
          ) as UmpireComponentBranding,
      ),
      map(branding => branding?.logo),
    );

  footer$ = this.store
    .dispatch(
      new LoadGlobalPageElementBySlug(UmpireGlobalPageElementSlugEnum.HEADER_FOOTER, {
        include: 'components,components.links',
      }),
    )
    .pipe(
      switchMap(() =>
        this.store.select(
          GlobalPageElementState.getGlobalPageElementBySlug(
            UmpireGlobalPageElementSlugEnum.HEADER_FOOTER,
          ),
        ),
      ),
      map(data => {
        return data?.components?.find(
          item => item.kind === UmpireComponentTypeEnum.PAGE_FOOTER,
        ) as UmpireComponentFooter;
      }),
      shareReplay(1),
    );

  footerDynamicLinks$ = combineLatest([
    this.footer$.pipe(
      filter(header => !!header),
      map(header => header?.links.map(link => link.resource.data)),
      map(links =>
        links.filter(
          link =>
            !(
              link.type === UmpireLinkTypeEnum.PAGE_TYPE &&
              link.attributes.slug === 'home'
            ),
        ),
      ),
    ),
    this.store
      .select(BlockedCountiresState.isInBlockedCountry)
      .pipe(filter(state => state !== null)),
  ]).pipe(
    map(([links, isInBlockedCountry]) =>
      links.map(link => {
        switch (link.type) {
          case UmpireLinkTypeEnum.PAGE_TYPE:
            return {
              label: link.attributes.name,
              routerLink: `${link.attributes.slug}`,
              hidden:
                this.templatePagesSlugsBlockedByCountry?.includes(link.attributes.slug) &&
                isInBlockedCountry,
            };
          case UmpireLinkTypeEnum.ARTICLE:
            return {
              label: link.attributes.title,
              routerLink: `page/${link.attributes.slug}`,
              // TODO: change to false when static pages are handled
              hidden: true,
            };
          case 'component':
            return {
              label: link.attributes.name,
              href: link.attributes.url,
              hidden: false,
            };
          default: {
            throw new Error(`Unsupported link type: ${link.type}`);
          }
        }
      }),
    ),
  );

  readonly staticLinks$ = of([
    {
      label: 'faq',
      routerLink: `/${RouteNames.faq}`,
      hidden: environment.name !== 'ekstraklasa',
    },
    {
      label: 'views.table',
      routerLink: `/${RouteNames.table}`,
      hidden: !environment.clientConfig.views?.showStandings,
    },
    {
      label: 'views.clubs',
      routerLink: `/${RouteNames.clubs}`,
      hidden: !environment.clientConfig.views?.showClubs,
    },
  ]);

  readonly links$ = combineLatest([this.footerDynamicLinks$, this.staticLinks$]).pipe(
    map(([footerDynamicLinks, staticLinks]) => [...footerDynamicLinks, ...staticLinks]),
  );

  constructor(
    readonly store: Store,
    @Inject(ASSET_MAP) readonly assetMap: AssetMap,
    readonly translatePipe: TranslatePipe,
    readonly translateService: TranslateService,
    readonly trackingService: TrackingService,
    readonly languageService: LanguageService,
  ) {}
}
